import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// DigestStatusButton.tsx -- top-bar button + dropdown panel that lists
// all digest jobs the user has triggered via document upload.
//
// Workbench I18 follow-up. Replaces the per-upload floating window
// with a single consolidated surface so uploading hundreds of files
// at once doesn't pollute the desktop with floating panels. The
// floating window is still available via right-click -> Status on
// any document icon for users who want to keep one specific file
// open while doing other work.
//
// User direction (verbatim, this session):
//   "it should be in the top header bar that can be clicked and
//    expanded or contracted, much like a downloads button on a
//    browser. When any upload is running it should show green of some
//    sort, if they're all done it should just show normal but the
//    user could expand it and see that they all completed, if
//    anything failed it should give an X"
//   "If there are no digets in the status window, when the user
//    expands it there should be a message that says 'upload a
//    document to begin diget'"
//   "have a button to detach it so if they want to make it floating
//    they could"
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDocumentStatusStore, usePanelStatusIds, } from '../../state/documentStatusStore.js';
import { DigestStatusPanelRow } from './DigestStatusPanelRow.js';
export function DigestStatusButton({ projectId }) {
    const docIds = usePanelStatusIds();
    const closeWindow = useDocumentStatusStore((s) => s.closeWindow);
    const panelDetached = useDocumentStatusStore((s) => s.panelDetached);
    const setPanelDetached = useDocumentStatusStore((s) => s.setPanelDetached);
    const panelPose = useDocumentStatusStore((s) => s.panelPose);
    const setPanelPose = useDocumentStatusStore((s) => s.setPanelPose);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const dragRef = useRef(null);
    // Drag handler for the detached window's header. Mirrors the
    // AssistantWindow / DocumentStatusWindow pattern. Skips clicks that
    // land on header buttons via target.closest('button').
    const onDetachedHeaderMouseDown = useCallback((e) => {
        if (e.target.closest('button'))
            return;
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            origX: panelPose.x,
            origY: panelPose.y,
        };
        const onMove = (ev) => {
            const s = dragRef.current;
            if (!s)
                return;
            const x = Math.max(8, Math.min(window.innerWidth - 360 - 8, s.origX + (ev.clientX - s.startX)));
            const y = Math.max(8, Math.min(window.innerHeight - 80, s.origY + (ev.clientY - s.startY)));
            setPanelPose({ x, y });
        };
        const onUp = () => {
            dragRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [panelPose.x, panelPose.y, setPanelPose]);
    // Row summaries roll up to drive the button color. Each row reports
    // its own current status via setRowSummary; we aggregate across
    // rows to compute the button's pill color (green if any in-flight,
    // red if any failed, neutral if all complete or none).
    //
    // CRITICAL: setRowSummary MUST be useCallback-stabilized. The
    // child rows + headless pollers list it in their useEffect deps
    // for the "report up" effect (e.g. DigestStatusPanelRow:103-105
    // and HeadlessSummaryPoller:349-351). If the function identity
    // changes on every parent render, those effects re-fire on every
    // parent render -> the report writes back into setRowSummaries ->
    // parent re-renders -> children's deps change again -> infinite
    // render loop ("Maximum update depth exceeded" warning, observed
    // 33k+ times before the fix).
    //
    // The setRowSummaries setter is itself stable (React guarantees),
    // and the functional updater inside doesn't read any closed-over
    // state, so an empty deps array gives us the right identity-stable
    // function with no stale closure risk.
    const [rowSummaries, setRowSummaries] = useState({});
    const setRowSummary = useCallback((s) => {
        setRowSummaries((prev) => {
            // Skip the state update entirely when nothing changed.
            // setState bails out for === values, but our value is a fresh
            // object every call so referential equality never holds.
            // Compare the documentId/status pair against the previous
            // entry; if equal, return prev to avoid the rerender. This
            // is a belt-and-suspenders measure on top of the useCallback
            // identity-stabilization above -- without it, every poll tick
            // (every 30s) would still cause one harmless re-render.
            const cur = prev[s.documentId];
            if (cur && cur.status === s.status)
                return prev;
            return { ...prev, [s.documentId]: s };
        });
    }, []);
    // Drop summaries for closed rows (don't leak memory across closes).
    useEffect(() => {
        setRowSummaries((prev) => {
            const next = {};
            for (const id of docIds)
                if (prev[id])
                    next[id] = prev[id];
            return next;
        });
    }, [docIds]);
    // Outside-click + Escape close the dropdown. Skipped when the panel
    // is detached (the floating window has its own close button and
    // shouldn't auto-dismiss on outside clicks).
    useEffect(() => {
        if (!open || panelDetached)
            return;
        const onDocClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, panelDetached]);
    // Aggregate state for the button color:
    //   any failed   -> 'failed' (red X)
    //   any active   -> 'active' (green, with count)
    //   any complete -> 'idle-with-history' (neutral, no count)
    //   none         -> 'empty' (neutral, no count)
    //
    // "In flight" = anything that's NOT terminal. The server's overall
    // verb is 'running' for any non-complete job; the per-stage vocab
    // uses 'streaming' / 'queued'. We accept all of those + 'unknown'
    // (initial-fetch state before the first response) so the button
    // stays green continuously while a digest is active.
    const summaryValues = Object.values(rowSummaries);
    const hasFailed = summaryValues.some((s) => s.status === 'failed');
    const inFlightCount = summaryValues.filter((s) => s.status !== 'complete' && s.status !== 'failed').length;
    const buttonState = hasFailed ? 'failed'
        : inFlightCount > 0 ? 'active'
            : docIds.length > 0 ? 'idle-history'
                : 'empty';
    // Button label: "Digest" with optional count or X. Browsers' download
    // buttons follow this same pattern (icon + active count).
    let label = 'Digest';
    if (buttonState === 'active')
        label = `Digest (${inFlightCount})`;
    else if (buttonState === 'failed')
        label = 'Digest (failed)';
    // Shared body content -- the rows themselves are the same whether
    // we're rendering a top-bar dropdown or a detached free-floating
    // window. Only the chrome (header + container positioning) differs.
    const bodyContent = (_jsx("div", { className: "digest-status-dropdown-body", children: docIds.length === 0 ? (_jsx("div", { className: "digest-status-empty", children: "Upload a document to begin digest." })) : (docIds.map((id) => (_jsx(DigestStatusPanelRow, { projectId: projectId, documentId: id, onClose: () => closeWindow(id, 'panel'), onSummary: setRowSummary }, id)))) }));
    return (_jsxs("div", { className: "digest-status-wrap", ref: wrapRef, children: [_jsxs("button", { type: "button", className: `digest-status-button digest-status-button-${buttonState}`, onClick: () => setOpen((v) => !v), "aria-expanded": open, title: panelDetached
                    ? 'Digest status (window is detached)'
                    : 'Digest status', children: [_jsx("span", { className: "digest-status-button-glyph", "aria-hidden": true, children: buttonState === 'failed' ? '\u2717' : '\u2630' }), _jsx("span", { className: "digest-status-button-label", children: label }), panelDetached && (_jsx("span", { className: "digest-status-button-detached", "aria-hidden": true, title: "Detached", children: '\u2197' }))] }), open && !panelDetached && (_jsxs("div", { className: "digest-status-dropdown", role: "dialog", "aria-label": "Digest status", children: [_jsxs("div", { className: "digest-status-dropdown-header", children: [_jsx("span", { className: "digest-status-dropdown-title", children: "Digest Status" }), _jsx("button", { type: "button", className: "digest-status-dropdown-detach", onClick: () => { setPanelDetached(true); }, title: "Detach (turn into a draggable window)", children: '\u2197' }), _jsx("button", { type: "button", className: "digest-status-dropdown-close", onClick: () => setOpen(false), title: "Close", children: '\u2715' })] }), bodyContent] })), panelDetached && open && (_jsxs("div", { className: "digest-status-detached", role: "dialog", "aria-label": "Digest status", style: { left: panelPose.x, top: panelPose.y }, children: [_jsxs("div", { className: "digest-status-dropdown-header digest-status-detached-header", onMouseDown: onDetachedHeaderMouseDown, children: [_jsx("span", { className: "digest-status-dropdown-title", children: "Digest Status" }), _jsx("button", { type: "button", className: "digest-status-dropdown-detach", onClick: () => { setPanelDetached(false); }, title: "Re-attach to the top bar", children: '\u21B2' }), _jsx("button", { type: "button", className: "digest-status-dropdown-close", onClick: () => setOpen(false), title: "Close", children: '\u2715' })] }), bodyContent] })), docIds.map((id) => (_jsx(HeadlessSummaryPoller, { projectId: projectId, documentId: id, onSummary: setRowSummary }, `hp:${id}`)))] }));
}
/**
 * Invisible component that polls a single document's status and
 * reports up via onSummary. Renders nothing. Used by the button to
 * keep its rolled-up color accurate even when the dropdown is closed.
 *
 * Mirrors the polling shape of DigestStatusPanelRow so the two hooks
 * see the same data. Could be unified into a shared hook later; for
 * now the duplication is small and very local.
 */
function HeadlessSummaryPoller({ projectId, documentId, onSummary, }) {
    const [overall, setOverall] = useState('unknown');
    const isTerminal = overall === 'complete' || overall === 'failed';
    const refresh = useCallback(async () => {
        try {
            const tok = localStorage.getItem('fdn:token');
            const headers = {};
            if (tok)
                headers.Authorization = `Bearer ${tok}`;
            const r = await fetch(`/api/p/${projectId}/documents/${documentId}/status`, {
                credentials: 'include',
                headers,
            });
            if (!r.ok)
                return;
            const data = await r.json();
            const next = (data.status?.overall ?? 'unknown');
            setOverall(next);
        }
        catch {
            /* swallow -- the visible row reports errors; the headless one
             *  just silently stays at its last known overall. */
        }
    }, [projectId, documentId]);
    // Initial fetch on mount.
    useEffect(() => { void refresh(); }, [refresh]);
    // Poll while not terminal.
    useEffect(() => {
        if (isTerminal)
            return;
        const id = setInterval(() => { void refresh(); }, 30_000);
        return () => clearInterval(id);
    }, [isTerminal, refresh]);
    // Report up to the button.
    useEffect(() => {
        onSummary({ documentId, status: overall });
    }, [documentId, overall, onSummary]);
    return null;
}
